import {
  updateUserPermissionsSchema,
  type UpdateUserPermissions,
  type ProductAnalyticsQuery,
} from '@controlstock/contracts';
import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  stockTrialHistorySchema,
  adjustmentBodySchema,
  adjustmentResponseSchema,
  arcaInvoicingSettingsSchema,
  bulkAdjustmentBodySchema,
  bulkAdjustmentResponseSchema,
  apiErrorSchema,
  authSessionSchema,
  bankAccountInputSchema,
  bankAccountsListSchema,
  bankAccountSchema,
  bankMovementInputSchema,
  bankMovementsListSchema,
  bankMovementSchema,
  channelSchema,
  checkInputSchema,
  checkSchema,
  checkbookInputSchema,
  checkbookSchema,
  checkbooksListSchema,
  checksListSchema,
  collectionInputSchema,
  collectionSchema,
  collectionsListSchema,
  createManualOrderSchema,
  createUserBodySchema,
  customerInputSchema,
  customerSchema,
  customersExportQuerySchema,
  customersListSchema,
  dashboardSchema,
  expenseCategoriesListSchema,
  expenseCategoryInputSchema,
  expenseCategorySchema,
  expenseInputSchema,
  expenseSchema,
  expensesListSchema,
  employeeInputSchema,
  employeeSchema,
  employeesListSchema,
  integrationAuthorizationSchema,
  mercadoLibreCatalogOptionsSchema,
  tiendanubeCategoriesSchema,
  inventoryExportQuerySchema,
  inventoryListSchema,
  inventorySyncProgressSchema,
  inventoryQuerySchema,
  productAnalyticsQuerySchema,
  productAnalyticsSchema,
  inventoryValuationQuerySchema,
  productGroupParamSchema,
  inventoryValuationSchema,
  invoicesListSchema,
  invoicesQuerySchema,
  invoiceCreateInputSchema,
  linkListingBodySchema,
  loginBodySchema,
  mediaUploadResponseSchema,
  meliStockTargetBodySchema,
  ordersListSchema,
  ordersQuerySchema,
  salesReportQuerySchema,
  salesReportSchema,
  orderCustomerSummarySchema,
  orderHistoryListSchema,
  paymentInputSchema,
  paymentSchema,
  paymentsListSchema,
  purchaseInputSchema,
  purchaseCreateInputSchema,
  purchaseDetailSchema,
  purchaseSchema,
  purchasesListSchema,
  stockTransferInputSchema,
  stockTransferResultSchema,
  inventoryReferenceDataSchema,
  productionOrderInputSchema,
  productionOrdersListSchema,
  productionReportInputSchema,
  workshopOrderInputSchema,
  workshopOrderReceiptInputSchema,
  workshopReceiptInputSchema,
  workshopReceiptsListSchema,
  workshopShipmentInputSchema,
  workshopShipmentsListSchema,
  workshopOrdersListSchema,
  sellerInputSchema,
  sellerSchema,
  sellersListSchema,
  supplierInputSchema,
  supplierSchema,
  suppliersListSchema,
  workshopProcessInputSchema,
  workshopProcessPriceInputSchema,
  workshopProcessPriceSchema,
  workshopProcessPricesListSchema,
  workshopProcessSchema,
  workshopProcessesListSchema,
  productProcessReferencesListSchema,
  rawMaterialInputSchema,
  rawMaterialSchema,
  rawMaterialsListSchema,
  productCompositionSchema,
  compositionProcessInputSchema,
  compositionOtherCostInputSchema,
  updateOrderWorkflowSchema,
  updateArcaInvoicingSettingsSchema,
  orderSyncResponseSchema,
  matchingRowSchema,
  paginationQuerySchema,
  productDetailSchema,
  productEditorSchema,
  productSaveResponseSchema,
  updateProductPublicationPolicySchema,
  reconcileBodySchema,
  syncJobSchema,
  userSchema,
} from '@controlstock/contracts';
import type {
  AdjustmentBody,
  ArcaInvoicingSettings,
  BulkAdjustmentBody,
  AuthSession,
  BankAccountInput,
  BankMovementInput,
  CollectionInput,
  CheckInput,
  CheckbookInput,
  CustomerInput,
  CustomersExportQuery,
  Channel,
  MercadoLibreCatalogOptions,
  Dashboard,
  InventoryExportQuery,
  InventoryQuery,
  InventoryValuation,
  InventoryValuationQuery,
  InvoicesQuery,
  InvoicesList,
  OrdersQuery,
  OrdersList,
  OrderSyncResponse,
  OrderCustomerSummary,
  OrderHistoryList,
  SalesReport,
  LoginBody,
  UpdateArcaInvoicingSettings,
  SellerInput,
  SupplierInput,
  CreateManualOrder,
} from '@controlstock/contracts';
import { z } from 'zod';
import {
  integrationsListSchema,
  matchingListSchema,
  matchingQuerySchema,
  movementsListSchema,
  movementsQuerySchema,
  onboardingSchema,
  queuedJobSchema,
  reconciliationRequestSchema,
  reconciliationHistoryListSchema,
  reconciliationHistoryQuerySchema,
  syncJobsListSchema,
  syncJobsQuerySchema,
  setOrganizationStateBodySchema,
  usersListSchema,
  usersQuerySchema,
} from './api-types';
import type {
  IntegrationAuthorization,
  IntegrationsList,
  MatchingList,
  MatchingQueryInput,
  MovementsList,
  MovementsQueryInput,
  Onboarding,
  QueuedJob,
  ReconciliationHistoryList,
  ReconciliationHistoryQueryInput,
  ReconciliationRequest,
  SyncJobsList,
  SyncJobsQueryInput,
  SetOrganizationStateInput,
  UsersList,
  UsersQueryInput,
} from './api-types';

const API_VERSION_PREFIX = '/v1';
const CSRF_HEADER = 'x-csrf-token';
const DEFAULT_TIMEOUT_MS = 15_000;

type ApiRequestErrorInit = Readonly<{
  code: string;
  message: string;
  status: number | undefined;
  requestId: string | undefined;
  details: Record<string, unknown> | undefined;
}>;

/** A normalized error safe to display in UI error states. */
export class ApiRequestError extends Error {
  public readonly code: string;
  public readonly status: number | undefined;
  public readonly requestId: string | undefined;
  public readonly details: Record<string, unknown> | undefined;

  public constructor({ code, message, status, requestId, details }: ApiRequestErrorInit) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.details = details;
  }
}

export function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError;
}

function toApiRequestError(error: unknown): ApiRequestError {
  if (error instanceof ApiRequestError) return error;

  if (axios.isAxiosError(error)) {
    const parsedError = apiErrorSchema.safeParse(error.response?.data);
    if (parsedError.success) {
      const payload = parsedError.data.error;
      return new ApiRequestError({
        code: payload.code,
        message: payload.message,
        status: error.response?.status,
        requestId: payload.requestId,
        details: payload.details,
      });
    }

    return new ApiRequestError({
      code: error.code ?? 'REQUEST_FAILED',
      message: error.message || 'No se pudo completar la solicitud.',
      status: error.response?.status,
      requestId: undefined,
      details: undefined,
    });
  }

  if (error instanceof Error) {
    return new ApiRequestError({
      code: 'REQUEST_FAILED',
      message: error.message || 'No se pudo completar la solicitud.',
      status: undefined,
      requestId: undefined,
      details: undefined,
    });
  }

  return new ApiRequestError({
    code: 'REQUEST_FAILED',
    message: 'No se pudo completar la solicitud.',
    status: undefined,
    requestId: undefined,
    details: undefined,
  });
}

function requiresCsrfToken(method: string | undefined): boolean {
  const normalizedMethod = method?.toUpperCase();
  return normalizedMethod !== undefined && !['GET', 'HEAD', 'OPTIONS'].includes(normalizedMethod);
}

let csrfToken: string | undefined;

type CsrfRetryRequestConfig = AxiosRequestConfig & { _csrfRetried?: boolean };

/**
 * Keeps the client copy of the server-issued CSRF token in memory only. The
 * server owns the companion double-submit cookie; this client never persists
 * the token in localStorage or sessionStorage.
 */
export function setCsrfToken(token: string | null | undefined): void {
  csrfToken = token || undefined;
}

export function clearCsrfToken(): void {
  csrfToken = undefined;
}

export function getCsrfToken(): string | undefined {
  return csrfToken;
}

function readConfiguredApiBaseUrl(): string | undefined {
  const environment: unknown = import.meta.env;
  if (environment === null || typeof environment !== 'object') return undefined;
  const value = (environment as Record<string, unknown>).VITE_API_BASE_URL;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

const configuredApiBaseUrl = readConfiguredApiBaseUrl();

/** The low-level same-origin Axios instance. Prefer `apiClient` in application code. */
export const httpClient: AxiosInstance = axios.create({
  baseURL: configuredApiBaseUrl || '/api',
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
});

httpClient.interceptors.request.use((config) => {
  if (csrfToken !== undefined && requiresCsrfToken(config.method)) {
    config.headers.set(CSRF_HEADER, csrfToken);
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const responseError = apiErrorSchema.safeParse(error.response?.data);
      const requestConfig = error.config as CsrfRetryRequestConfig | undefined;
      const isCsrfError =
        responseError.success &&
        responseError.data.error.code === 'FORBIDDEN' &&
        responseError.data.error.message === 'El token CSRF no es válido';

      if (
        isCsrfError &&
        requestConfig &&
        !requestConfig._csrfRetried &&
        requiresCsrfToken(requestConfig.method)
      ) {
        requestConfig._csrfRetried = true;
        try {
          const sessionResponse = await httpClient.get('/v1/auth/session');
          const session = authSessionSchema.parse(sessionResponse.data);
          setCsrfToken(session.csrfToken);
          const headers = axios.AxiosHeaders.from(
            requestConfig.headers as Parameters<typeof axios.AxiosHeaders.from>[0],
          );
          headers.set(CSRF_HEADER, session.csrfToken);
          requestConfig.headers = headers;
          return await httpClient.request(requestConfig);
        } catch (refreshError) {
          return Promise.reject(toApiRequestError(refreshError));
        }
      }
    }
    return Promise.reject(toApiRequestError(error));
  },
);

async function request<TSchema extends z.ZodType>(
  config: AxiosRequestConfig,
  responseSchema: TSchema,
): Promise<z.output<TSchema>> {
  const response = await httpClient.request<unknown>(config);
  try {
    return responseSchema.parse(response.data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new ApiRequestError({
        code: 'INVALID_API_RESPONSE',
        message: 'El servidor devolvió una respuesta inesperada.',
        status: response.status,
        requestId: undefined,
        details: undefined,
      });
    }
    if (error instanceof Error) throw error;
    throw new ApiRequestError({
      code: 'INVALID_API_RESPONSE',
      message: 'El servidor devolvió una respuesta inesperada.',
      status: response.status,
      requestId: undefined,
      details: undefined,
    });
  }
}

async function requestVoid(config: AxiosRequestConfig): Promise<void> {
  await httpClient.request(config);
}

function identifierPathSegment(id: string): string {
  return encodeURIComponent(z.string().uuid().parse(id));
}

function integrationChannelPathSegment(channel: Channel): 'tiendanube' | 'mercadolibre' {
  return channelSchema.parse(channel) === 'TIENDANUBE' ? 'tiendanube' : 'mercadolibre';
}

function definedQueryParams(
  input: Record<string, string | number | boolean | undefined>,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) params[key] = value;
  }
  return params;
}

async function downloadInvoicePdf(
  invoiceId: string,
  pathPrefix: string,
): Promise<{ blob: Blob; filename: string }> {
  const id = z.string().uuid().parse(invoiceId);
  const response = await httpClient.request<Blob>({
    method: 'GET',
    url: `${pathPrefix}/${id}.pdf`,
    responseType: 'blob',
    timeout: 60_000,
  });
  const disposition: unknown = response.headers['content-disposition'];
  const filenameMatch =
    typeof disposition === 'string' ? /filename="?([^";]+)"?/i.exec(disposition) : null;
  return {
    blob: response.data,
    filename: filenameMatch?.[1] ?? `factura-${id}.pdf`,
  };
}

export type InventoryQueryInput = z.input<typeof inventoryQuerySchema>;
export type InventoryExportQueryInput = z.input<typeof inventoryExportQuerySchema>;
export type InventoryValuationQueryInput = z.input<typeof inventoryValuationQuerySchema>;
export type ProductListQueryInput = z.input<typeof paginationQuerySchema>;
export type AdjustmentResponse = z.output<typeof adjustmentResponseSchema>;
export type BulkAdjustmentResponse = z.output<typeof bulkAdjustmentResponseSchema>;
export type InventoryList = z.output<typeof inventoryListSchema>;
export type ProductDetail = z.output<typeof productDetailSchema>;
export type ProductEditor = z.input<typeof productEditorSchema>;
export type LinkListingInput = z.input<typeof linkListingBodySchema>;
export type MeliStockTargetInput = z.input<typeof meliStockTargetBodySchema>;
export type IntegrationStatus = IntegrationsList[number];
export type SyncJob = z.output<typeof syncJobSchema>;
export type CreateUserInput = z.input<typeof createUserBodySchema>;
export type ReconcileInput = z.input<typeof reconcileBodySchema>;
export type OrdersQueryInput = z.input<typeof ordersQuerySchema>;
export type InvoicesQueryInput = z.input<typeof invoicesQuerySchema>;
export type SalesReportQueryInput = z.input<typeof salesReportQuerySchema>;
export type MediaUploadResponse = z.output<typeof mediaUploadResponseSchema>;

export const apiClient = {
  auth: {
    getSession: (): Promise<AuthSession> =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/auth/session` }, authSessionSchema),

    login: (body: LoginBody): Promise<AuthSession> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/auth/login`,
          data: loginBodySchema.parse(body),
        },
        authSessionSchema,
      ),

    logout: (): Promise<void> =>
      requestVoid({ method: 'POST', url: `${API_VERSION_PREFIX}/auth/logout` }),
  },

  dashboard: {
    get: (): Promise<Dashboard> =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/dashboard` }, dashboardSchema),
    listOrders: (query: OrdersQueryInput = {}): Promise<OrdersList> => {
      const parsedQuery = ordersQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/dashboard/orders`,
          params: definedQueryParams(parsedQuery),
        },
        ordersListSchema,
      );
    },
    listInvoices: (query: InvoicesQueryInput = {}): Promise<InvoicesList> => {
      const parsedQuery = invoicesQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/dashboard/invoices`,
          params: definedQueryParams(parsedQuery),
        },
        invoicesListSchema,
      );
    },
    downloadInvoicePdf: (invoiceId: string): Promise<{ blob: Blob; filename: string }> =>
      downloadInvoicePdf(invoiceId, `${API_VERSION_PREFIX}/dashboard/invoices`),
    listCustomers: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/dashboard/customers` },
        customersListSchema,
      ),
    listCollections: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/dashboard/collections` },
        collectionsListSchema,
      ),
  },

  banking: {
    listAccounts: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/bank-accounts` },
        bankAccountsListSchema,
      ),
    createAccount: (body: BankAccountInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/bank-accounts`,
          data: bankAccountInputSchema.parse(body),
        },
        bankAccountSchema,
      ),
    updateAccount: (id: string, body: BankAccountInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/bank-accounts/${identifierPathSegment(id)}`,
          data: bankAccountInputSchema.parse(body),
        },
        bankAccountSchema,
      ),
    listMovements: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/bank-movements` },
        bankMovementsListSchema,
      ),
    createMovement: (body: BankMovementInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/bank-movements`,
          data: bankMovementInputSchema.parse(body),
        },
        bankMovementSchema,
      ),
  },

  checks: {
    listCheckbooks: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/checkbooks` }, checkbooksListSchema),
    createCheckbook: (body: CheckbookInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/checkbooks`,
          data: checkbookInputSchema.parse(body),
        },
        checkbookSchema,
      ),
    updateCheckbook: (id: string, body: CheckbookInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/checkbooks/${identifierPathSegment(id)}`,
          data: checkbookInputSchema.parse(body),
        },
        checkbookSchema,
      ),
    deactivateCheckbook: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/checkbooks/${identifierPathSegment(id)}`,
      }),
    list: () => request({ method: 'GET', url: `${API_VERSION_PREFIX}/checks` }, checksListSchema),
    create: (body: CheckInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/checks`,
          data: checkInputSchema.parse(body),
        },
        checkSchema,
      ),
    update: (id: string, body: CheckInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/checks/${identifierPathSegment(id)}`,
          data: checkInputSchema.parse(body),
        },
        checkSchema,
      ),
    cancel: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/checks/${identifierPathSegment(id)}`,
      }),
  },

  inventory: {
    syncProgress: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/inventory/sync-progress` },
        inventorySyncProgressSchema,
      ),
    stockTrial: (page: number) =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/inventory/stock-trial`,
          params: { page, pageSize: 25 },
        },
        stockTrialHistorySchema,
      ),
    snapshotStockTrial: () =>
      request(
        { method: 'POST', url: `${API_VERSION_PREFIX}/inventory/stock-trial` },
        z.object({ id: z.string().uuid() }),
      ),
    list: (query: InventoryQueryInput = {}, signal?: AbortSignal): Promise<InventoryList> => {
      const parsedQuery = inventoryQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/inventory`,
          params: definedQueryParams(parsedQuery),
          ...(signal ? { signal } : {}),
        },
        inventoryListSchema,
      );
    },

    exportXlsx: async (
      query: InventoryExportQueryInput = {},
    ): Promise<{ blob: Blob; filename: string }> => {
      const parsedQuery: InventoryExportQuery = inventoryExportQuerySchema.parse(query);
      const response = await httpClient.request<Blob>({
        method: 'GET',
        url: `${API_VERSION_PREFIX}/inventory/export.xlsx`,
        params: definedQueryParams(parsedQuery),
        responseType: 'blob',
        timeout: 60_000,
      });
      const disposition: unknown = response.headers['content-disposition'];
      const filenameMatch =
        typeof disposition === 'string' ? /filename="?([^";]+)"?/i.exec(disposition) : null;
      return {
        blob: response.data,
        filename: filenameMatch?.[1] ?? 'inventario.xlsx',
      };
    },

    valuation: (query: InventoryValuationQueryInput = {}): Promise<InventoryValuation> => {
      const parsedQuery: InventoryValuationQuery = inventoryValuationQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/inventory/valuation`,
          params: definedQueryParams(parsedQuery),
        },
        inventoryValuationSchema,
      );
    },

    createAdjustment: (body: AdjustmentBody): Promise<AdjustmentResponse> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/inventory/adjustments`,
          data: adjustmentBodySchema.parse(body),
        },
        adjustmentResponseSchema,
      ),

    createBulkAdjustment: (body: BulkAdjustmentBody): Promise<BulkAdjustmentResponse> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/inventory/adjustments/bulk`,
          data: bulkAdjustmentBodySchema.parse(body),
        },
        bulkAdjustmentResponseSchema,
      ),

    transferStock: (body: z.input<typeof stockTransferInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/inventory/transfers`,
          data: stockTransferInputSchema.parse(body),
        },
        stockTransferResultSchema,
      ),
    referenceData: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/inventory/reference-data` },
        inventoryReferenceDataSchema,
      ),
  },

  products: {
    analytics: (query: ProductAnalyticsQuery) => {
      const parsedQuery = productAnalyticsQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/products/analytics`,
          params: definedQueryParams(parsedQuery),
        },
        productAnalyticsSchema,
      );
    },
    create: (body: ProductEditor): Promise<z.output<typeof productSaveResponseSchema>> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/products`,
          data: productEditorSchema.parse(body),
        },
        productSaveResponseSchema,
      ),

    list: (query: ProductListQueryInput = {}): Promise<InventoryList> => {
      const parsedQuery = paginationQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/products`,
          params: definedQueryParams(parsedQuery),
        },
        inventoryListSchema,
      );
    },

    getDetail: (productId: string): Promise<ProductDetail> =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}`,
        },
        productDetailSchema,
      ),

    getGroupDetail: (groupCode: string): Promise<ProductDetail> => {
      const code = productGroupParamSchema.shape.code.parse(groupCode);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/products/group/${encodeURIComponent(code)}`,
        },
        productDetailSchema,
      );
    },

    updateData: (
      productId: string,
      body: ProductEditor,
    ): Promise<z.output<typeof productSaveResponseSchema>> =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/data`,
          data: productEditorSchema.parse(body),
        },
        productSaveResponseSchema,
      ),

    updatePublicationPolicy: (
      productId: string,
      publicationPolicy: ProductDetail['publicationPolicy'],
    ): Promise<ProductDetail['publicationPolicy']> =>
      request(
        {
          method: 'PATCH',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/publication-policy`,
          data: updateProductPublicationPolicySchema.parse({ publicationPolicy }),
        },
        updateProductPublicationPolicySchema,
      ).then((result) => result.publicationPolicy),
  },

  media: {
    uploadImage: (file: File): Promise<MediaUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/media/images`,
          data: formData,
        },
        mediaUploadResponseSchema,
      );
    },
    uploadVideo: (file: File): Promise<MediaUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/media/videos`,
          data: formData,
        },
        mediaUploadResponseSchema,
      );
    },
  },

  matching: {
    list: async (query: MatchingQueryInput = {}): Promise<MatchingList> => {
      const parsedQuery = matchingQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/matching`,
          params: definedQueryParams(parsedQuery),
        },
        matchingListSchema,
      );
    },

    linkListing: (
      listingId: string,
      body: LinkListingInput,
    ): Promise<z.output<typeof matchingRowSchema>> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/matching/${identifierPathSegment(listingId)}/link`,
          data: linkListingBodySchema.parse(body),
        },
        matchingRowSchema,
      ),

    setMeliStockTarget: (
      listingId: string,
      body: MeliStockTargetInput,
    ): Promise<z.output<typeof matchingRowSchema>> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/matching/${identifierPathSegment(listingId)}/stock-target`,
          data: meliStockTargetBodySchema.parse(body),
        },
        matchingRowSchema,
      ),
  },

  integrations: {
    list: (): Promise<IntegrationsList> =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/integrations` }, integrationsListSchema),

    beginAuthorization: (channel: Channel): Promise<IntegrationAuthorization> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/integrations/${integrationChannelPathSegment(channel)}/connect`,
        },
        integrationAuthorizationSchema,
      ),

    synchronize: (
      input: Readonly<{ channel: Channel; connectionId?: string }>,
    ): Promise<QueuedJob> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/integrations/${integrationChannelPathSegment(input.channel)}/sync`,
          data: input.connectionId === undefined ? {} : { connectionId: input.connectionId },
        },
        queuedJobSchema,
      ),

    mercadoLibreCatalogOptions: (
      query: Readonly<{ q?: string; categoryId?: string }> = {},
    ): Promise<MercadoLibreCatalogOptions> =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/integrations/mercadolibre/catalog-options`,
          params: definedQueryParams(query),
        },
        mercadoLibreCatalogOptionsSchema,
      ),

    tiendanubeCategories: (): Promise<z.output<typeof tiendanubeCategoriesSchema>> =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/integrations/tiendanube/categories`,
        },
        tiendanubeCategoriesSchema,
      ),
  },

  jobs: {
    list: (query: SyncJobsQueryInput = {}): Promise<SyncJobsList> => {
      const parsedQuery = syncJobsQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/jobs`,
          params: definedQueryParams(parsedQuery),
        },
        syncJobsListSchema,
      );
    },

    retry: (jobId: string): Promise<SyncJob> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/jobs/${identifierPathSegment(jobId)}/retry`,
        },
        syncJobSchema,
      ),
  },

  users: {
    setPermissions: (id: string, body: UpdateUserPermissions): Promise<void> =>
      requestVoid({
        method: 'PUT',
        url: `${API_VERSION_PREFIX}/users/${identifierPathSegment(id)}/permissions`,
        data: updateUserPermissionsSchema.parse(body),
      }),
    list: (query: UsersQueryInput = {}): Promise<UsersList> => {
      const parsedQuery = usersQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/users`,
          params: definedQueryParams(parsedQuery),
        },
        usersListSchema,
      );
    },

    create: (body: CreateUserInput): Promise<z.output<typeof userSchema>> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/users`,
          data: createUserBodySchema.parse(body),
        },
        userSchema,
      ),
  },

  employees: {
    list: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/employees` }, employeesListSchema),
    create: (body: z.input<typeof employeeInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/employees`,
          data: employeeInputSchema.parse(body),
        },
        employeeSchema,
      ),
    update: (id: string, body: z.input<typeof employeeInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/employees/${identifierPathSegment(id)}`,
          data: employeeInputSchema.parse(body),
        },
        employeeSchema,
      ),
    delete: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/employees/${identifierPathSegment(id)}`,
      }),
  },

  orders: {
    list: (query: OrdersQueryInput = {}): Promise<OrdersList> => {
      const parsedQuery = ordersQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/orders`,
          params: definedQueryParams(parsedQuery),
        },
        ordersListSchema,
      );
    },
    customerSummary: (customerId: string): Promise<OrderCustomerSummary> =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/orders/summary`,
          params: { customerId },
        },
        orderCustomerSummarySchema,
      ),
    listHistory: (
      customerId: string,
      query: { page?: number; pageSize?: number } = {},
    ): Promise<OrderHistoryList> =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/orders/history`,
          params: { customerId, page: query.page ?? 1, pageSize: query.pageSize ?? 10 },
        },
        orderHistoryListSchema,
      ),

    createManual: (body: CreateManualOrder) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/orders/manual`,
          data: createManualOrderSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),

    updateWorkflow: (id: string, body: z.input<typeof updateOrderWorkflowSchema>) =>
      requestVoid({
        method: 'PATCH',
        url: `${API_VERSION_PREFIX}/orders/${identifierPathSegment(id)}/workflow`,
        data: updateOrderWorkflowSchema.parse(body),
      }),

    synchronize: (): Promise<OrderSyncResponse> =>
      request(
        { method: 'POST', url: `${API_VERSION_PREFIX}/orders/sync` },
        orderSyncResponseSchema,
      ),
  },

  reports: {
    sales: (query: SalesReportQueryInput): Promise<SalesReport> => {
      const parsedQuery = salesReportQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/reports/sales`,
          params: definedQueryParams(parsedQuery),
        },
        salesReportSchema,
      );
    },
  },

  invoices: {
    list: (query: InvoicesQueryInput = {}): Promise<InvoicesList> => {
      const parsedQuery = invoicesQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/invoices`,
          params: definedQueryParams(parsedQuery),
        },
        invoicesListSchema,
      );
    },

    createDocument: (body: z.input<typeof invoiceCreateInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/invoices`,
          data: invoiceCreateInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),

    issueAsConsumerFinal: (invoiceId: string) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/invoices/${identifierPathSegment(invoiceId)}/consumer-final`,
        },
        z.object({ id: z.string().uuid() }),
      ),

    downloadPdf: (invoiceId: string): Promise<{ blob: Blob; filename: string }> =>
      downloadInvoicePdf(invoiceId, `${API_VERSION_PREFIX}/invoices`),
  },

  business: {
    listCustomers: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/customers` }, customersListSchema),
    exportCustomersXlsx: async (
      query: CustomersExportQuery = {},
    ): Promise<{ blob: Blob; filename: string }> => {
      const parsedQuery = customersExportQuerySchema.parse(query);
      const response = await httpClient.request<Blob>({
        method: 'GET',
        url: `${API_VERSION_PREFIX}/customers/export.xlsx`,
        params: definedQueryParams(parsedQuery),
        responseType: 'blob',
        timeout: 60_000,
      });
      const disposition: unknown = response.headers['content-disposition'];
      const filenameMatch =
        typeof disposition === 'string' ? /filename="?([^";]+)"?/i.exec(disposition) : null;
      return {
        blob: response.data,
        filename: filenameMatch?.[1] ?? 'clientes.xlsx',
      };
    },
    createCustomer: (body: CustomerInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/customers`,
          data: customerInputSchema.parse(body),
        },
        customerSchema,
      ),
    updateCustomer: (id: string, body: CustomerInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/customers/${identifierPathSegment(id)}`,
          data: customerInputSchema.parse(body),
        },
        customerSchema,
      ),
    deleteCustomer: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/customers/${identifierPathSegment(id)}`,
      }),
    listSellers: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/sellers` }, sellersListSchema),
    createSeller: (body: SellerInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/sellers`,
          data: sellerInputSchema.parse(body),
        },
        sellerSchema,
      ),
    updateSeller: (id: string, body: SellerInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/sellers/${identifierPathSegment(id)}`,
          data: sellerInputSchema.parse(body),
        },
        sellerSchema,
      ),
    deleteSeller: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/sellers/${identifierPathSegment(id)}`,
      }),
    listSuppliers: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/suppliers` }, suppliersListSchema),
    createSupplier: (body: SupplierInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/suppliers`,
          data: supplierInputSchema.parse(body),
        },
        supplierSchema,
      ),
    updateSupplier: (id: string, body: SupplierInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/suppliers/${identifierPathSegment(id)}`,
          data: supplierInputSchema.parse(body),
        },
        supplierSchema,
      ),
    deleteSupplier: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/suppliers/${identifierPathSegment(id)}`,
      }),
    listWorkshopProcesses: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/workshop-processes` },
        workshopProcessesListSchema,
      ),
    listWorkshopProcessPrices: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/workshop-process-prices` },
        workshopProcessPricesListSchema,
      ),
    upsertWorkshopProcessPrice: (body: z.input<typeof workshopProcessPriceInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/workshop-process-prices`,
          data: workshopProcessPriceInputSchema.parse(body),
        },
        workshopProcessPriceSchema,
      ),
    listProductProcessReferences: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/product-process-references` },
        productProcessReferencesListSchema,
      ),
    createWorkshopProcess: (body: z.input<typeof workshopProcessInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/workshop-processes`,
          data: workshopProcessInputSchema.parse(body),
        },
        workshopProcessSchema,
      ),
    updateWorkshopProcess: (id: string, body: z.input<typeof workshopProcessInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/workshop-processes/${identifierPathSegment(id)}`,
          data: workshopProcessInputSchema.parse(body),
        },
        workshopProcessSchema,
      ),
    deleteWorkshopProcess: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/workshop-processes/${identifierPathSegment(id)}`,
      }),
    listCollections: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/collections` }, collectionsListSchema),
    createCollection: (body: CollectionInput) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/collections`,
          data: collectionInputSchema.parse(body),
        },
        collectionSchema,
      ),
    updateCollection: (id: string, body: CollectionInput) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/collections/${identifierPathSegment(id)}`,
          data: collectionInputSchema.parse(body),
        },
        collectionSchema,
      ),
    cancelCollection: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/collections/${identifierPathSegment(id)}`,
      }),
    listPurchases: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/purchases` }, purchasesListSchema),
    getPurchase: (id: string) =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/purchases/${identifierPathSegment(id)}`,
        },
        purchaseDetailSchema,
      ),
    createPurchase: (body: z.input<typeof purchaseCreateInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/purchases`,
          data: purchaseCreateInputSchema.parse(body),
        },
        purchaseSchema,
      ),
    updatePurchase: (id: string, body: z.input<typeof purchaseInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/purchases/${identifierPathSegment(id)}`,
          data: purchaseInputSchema.parse(body),
        },
        purchaseSchema,
      ),
    cancelPurchase: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/purchases/${identifierPathSegment(id)}`,
      }),
    listPayments: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/payments` }, paymentsListSchema),
    createPayment: (body: z.input<typeof paymentInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/payments`,
          data: paymentInputSchema.parse(body),
        },
        paymentSchema,
      ),
    updatePayment: (id: string, body: z.input<typeof paymentInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/payments/${identifierPathSegment(id)}`,
          data: paymentInputSchema.parse(body),
        },
        paymentSchema,
      ),
    cancelPayment: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/payments/${identifierPathSegment(id)}`,
      }),
    listExpenses: () =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/expenses` }, expensesListSchema),
    createExpense: (body: z.input<typeof expenseInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/expenses`,
          data: expenseInputSchema.parse(body),
        },
        expenseSchema,
      ),
    updateExpense: (id: string, body: z.input<typeof expenseInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/expenses/${identifierPathSegment(id)}`,
          data: expenseInputSchema.parse(body),
        },
        expenseSchema,
      ),
    deleteExpense: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/expenses/${identifierPathSegment(id)}`,
      }),
    listExpenseCategories: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/expense-categories` },
        expenseCategoriesListSchema,
      ),
    createExpenseCategory: (body: z.input<typeof expenseCategoryInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/expense-categories`,
          data: expenseCategoryInputSchema.parse(body),
        },
        expenseCategorySchema,
      ),
    updateExpenseCategory: (id: string, body: z.input<typeof expenseCategoryInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/expense-categories/${identifierPathSegment(id)}`,
          data: expenseCategoryInputSchema.parse(body),
        },
        expenseCategorySchema,
      ),
    deleteExpenseCategory: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/expense-categories/${identifierPathSegment(id)}`,
      }),
  },

  composition: {
    listRawMaterials: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/raw-materials` },
        rawMaterialsListSchema,
      ),
    createRawMaterial: (body: z.input<typeof rawMaterialInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/raw-materials`,
          data: rawMaterialInputSchema.parse(body),
        },
        rawMaterialSchema,
      ),
    updateRawMaterial: (id: string, body: z.input<typeof rawMaterialInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/raw-materials/${identifierPathSegment(id)}`,
          data: rawMaterialInputSchema.parse(body),
        },
        rawMaterialSchema,
      ),
    deleteRawMaterial: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/raw-materials/${identifierPathSegment(id)}`,
      }),
    get: (productId: string) =>
      request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition`,
        },
        productCompositionSchema,
      ),
    addMaterial: (productId: string, body: { rawMaterialId: string; quantity: number }) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition/materials`,
          data: body,
        },
        productCompositionSchema,
      ),
    removeMaterial: (productId: string, id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition/materials/${identifierPathSegment(id)}`,
      }),
    addOtherCost: (
      productId: string,
      body: { description: string; category: string; quantity: number; amount: number },
    ) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition/costs`,
          data: compositionOtherCostInputSchema.parse(body),
        },
        productCompositionSchema,
      ),
    removeOtherCost: (productId: string, id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition/costs/${identifierPathSegment(id)}`,
      }),
    addProcess: (productId: string, body: z.input<typeof compositionProcessInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition/processes`,
          data: compositionProcessInputSchema.parse(body),
        },
        productCompositionSchema,
      ),
    removeProcess: (productId: string, id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/products/${identifierPathSegment(productId)}/composition/processes/${identifierPathSegment(id)}`,
      }),
  },

  production: {
    listOrders: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/production/orders` },
        productionOrdersListSchema,
      ),
    createOrder: (body: z.input<typeof productionOrderInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/production/orders`,
          data: productionOrderInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    updateOrder: (id: string, body: z.input<typeof productionOrderInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/production/orders/${identifierPathSegment(id)}`,
          data: productionOrderInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    deleteOrder: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/production/orders/${identifierPathSegment(id)}`,
      }),
    report: (body: z.input<typeof productionReportInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/production/reports`,
          data: productionReportInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    listWorkshops: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/production/workshops` },
        workshopOrdersListSchema,
      ),
    listShipments: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/production/shipments` },
        workshopShipmentsListSchema,
      ),
    createShipment: (body: z.input<typeof workshopShipmentInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/production/shipments`,
          data: workshopShipmentInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    listReceipts: () =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/production/receipts` },
        workshopReceiptsListSchema,
      ),
    createReceipt: (body: z.input<typeof workshopReceiptInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/production/receipts`,
          data: workshopReceiptInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    updateReceipt: (id: string, body: z.input<typeof workshopReceiptInputSchema>) =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/production/receipts/${identifierPathSegment(id)}`,
          data: workshopReceiptInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    deleteReceipt: (id: string) =>
      requestVoid({
        method: 'DELETE',
        url: `${API_VERSION_PREFIX}/production/receipts/${identifierPathSegment(id)}`,
      }),
    createWorkshop: (body: z.input<typeof workshopOrderInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/production/workshops`,
          data: workshopOrderInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
    receiveWorkshop: (id: string, body: z.input<typeof workshopOrderReceiptInputSchema>) =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/production/workshops/${identifierPathSegment(id)}/receive`,
          data: workshopOrderReceiptInputSchema.parse(body),
        },
        z.object({ id: z.string().uuid() }),
      ),
  },

  movements: {
    list: (query: MovementsQueryInput = {}): Promise<MovementsList> => {
      const parsedQuery = movementsQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/movements`,
          params: definedQueryParams(parsedQuery),
        },
        movementsListSchema,
      );
    },
  },

  reconciliation: {
    run: (body: ReconcileInput = {}): Promise<ReconciliationRequest> =>
      request(
        {
          method: 'POST',
          url: `${API_VERSION_PREFIX}/reconciliation`,
          data: reconcileBodySchema.parse(body),
        },
        reconciliationRequestSchema,
      ),

    listHistory: (
      query: ReconciliationHistoryQueryInput = {},
    ): Promise<ReconciliationHistoryList> => {
      const parsedQuery = reconciliationHistoryQuerySchema.parse(query);
      return request(
        {
          method: 'GET',
          url: `${API_VERSION_PREFIX}/reconciliation`,
          params: definedQueryParams(parsedQuery),
        },
        reconciliationHistoryListSchema,
      );
    },
  },

  onboarding: {
    previewStock: () =>
      request(
        { method: 'POST', url: `${API_VERSION_PREFIX}/onboarding/stock-preview`, timeout: 180_000 },
        z.object({
          hash: z.string(),
          capturedAt: z.string(),
          total: z.number(),
          rows: z.array(z.object({ sku: z.string(), quantity: z.number() })),
        }),
      ),
    setStockScope: (body: {
      skus: string[];
      publications: string[];
      mercadoLibreConnectionId: string | null;
      mercadoLibreStockPolicy?: 'REFERENCE' | 'MINIMUM';
    }): Promise<void> =>
      requestVoid({
        method: 'PUT',
        url: `${API_VERSION_PREFIX}/onboarding/stock-scope`,
        data: body,
      }),
    get: (): Promise<Onboarding> =>
      request({ method: 'GET', url: `${API_VERSION_PREFIX}/onboarding` }, onboardingSchema),

    setOrganizationState: (body: SetOrganizationStateInput): Promise<void> =>
      requestVoid({
        method: 'POST',
        url: `${API_VERSION_PREFIX}/onboarding/state`,
        timeout: 180_000,
        data: setOrganizationStateBodySchema.parse(body),
      }),
  },

  settings: {
    getArcaInvoicing: (): Promise<ArcaInvoicingSettings> =>
      request(
        { method: 'GET', url: `${API_VERSION_PREFIX}/settings/arca-invoicing` },
        arcaInvoicingSettingsSchema,
      ),

    updateArcaInvoicing: (body: UpdateArcaInvoicingSettings): Promise<ArcaInvoicingSettings> =>
      request(
        {
          method: 'PUT',
          url: `${API_VERSION_PREFIX}/settings/arca-invoicing`,
          data: updateArcaInvoicingSettingsSchema.parse(body),
        },
        arcaInvoicingSettingsSchema,
      ),
  },
} as const;

export type {
  InventoryQuery,
  OrdersQuery,
  OrdersList,
  OrderSyncResponse,
  InvoicesQuery,
  InvoicesList,
};
export type {
  IntegrationAuthorization,
  IntegrationsList,
  MatchingList,
  MovementsList,
  Onboarding,
  QueuedJob,
  ReconciliationHistoryList,
  ReconciliationRequest,
  SyncJobsList,
  SetOrganizationStateInput,
  UsersList,
} from './api-types';
